import React, { useState, useCallback, useMemo } from "react";
import {
  Modal,
  Button,
  Box,
  Text,
  Table,
  ScrollArea,
  Switch,
  Loader,
  Group,
  Title,
  Flex,
  Stack,
  Anchor,
} from "@mantine/core";
import { Dropzone, FileWithPath } from "@mantine/dropzone";
import { IconUpload, IconX, IconPhoto, IconDownload, IconFileText } from "@tabler/icons";
import Papa from "papaparse";
import { useInviteBulkUsers } from "../../hooks/useSettings";
import { UserInvite } from "../../api/settings";
import { showNotification } from "@mantine/notifications";

interface BulkInvitePopupProps {
  isOpen: boolean;
  onClose: () => void;
}

const BulkInvitePopup: React.FC<BulkInvitePopupProps> = ({ isOpen, onClose }) => {
  const [users, setUsers] = useState<UserInvite[]>([]);
  const [sendInvites, setSendInvites] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { mutateAsync: inviteUsers } = useInviteBulkUsers();

  const handleDrop = useCallback((files: FileWithPath[]) => {
    const file = files[0];
    if (file) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const parsedUsers = results.data.map((row: any) => ({
            name: row.name || "",
            email: row.email || "",
            role: row.role || undefined,
            team: row.team || undefined,
          }));
          setUsers(parsedUsers);
        },
        error: (error) => {
          showNotification({
            title: "Error parsing CSV",
            message: error.message,
            color: "red",
          });
        },
      });
    }
  }, []);

  const handleSubmit = async () => {
    if (users.length === 0) {
      showNotification({
        title: "No users to invite",
        message: "Please upload a CSV file with user data",
        color: "yellow",
      });
      return;
    }

    try {
      setIsLoading(true);
      const result = await inviteUsers({
        users,
        options: {
          sendInvite: sendInvites,
          defaultRole: "manager",
        },
      });

      showNotification({
        title: "Users invited successfully",
        message: `Processed ${users.length} user invitations`,
        color: "green",
      });
      
      setUsers([]);
      onClose();
    } catch (error) {
      showNotification({
        title: "Error inviting users",
        message: error instanceof Error ? error.message : "Unknown error occurred",
        color: "red",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const downloadTemplate = () => {
    // Create template data
    const template = [
      {
        name: "John Doe",
        email: "john@example.com",
        role: "manager",
        team: "Sales"
      },
      {
        name: "Jane Smith",
        email: "jane@example.com",
        role: "agent",
        team: "Support"
      }
    ];
    
    // Convert to CSV
    const csv = Papa.unparse(template);
    
    // Create download link
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'bulk_invite_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const validUsers = useMemo(() => {
    return users.filter((user) => user.email && user.name);
  }, [users]);

  return (
    <Modal 
      opened={isOpen} 
      onClose={onClose} 
      size="xl" 
      title="Bulk Invite Users"
      closeOnClickOutside={!isLoading}
      centered
    >
      {users.length === 0 ? (
        <Dropzone
          onDrop={handleDrop}
          maxSize={5 * 1024 ** 2}
          accept={["text/csv"]}
          p={20}
        >
          <Group position="center" spacing="xl" style={{ minHeight: 150, pointerEvents: 'none' }}>
            <Dropzone.Accept>
              <IconUpload
                size={50}
                stroke={1.5}
                color="var(--mantine-color-blue-6)"
              />
            </Dropzone.Accept>
            <Dropzone.Reject>
              <IconX
                size={50}
                stroke={1.5}
                color="var(--mantine-color-red-6)"
              />
            </Dropzone.Reject>
            <Dropzone.Idle>
              <IconUpload size={50} stroke={1.5} />
            </Dropzone.Idle>

            <div>
              <Text size="xl" inline>
                  Drag CSV file here or click to select
              </Text>
              <Text size="sm" color="dimmed" inline mt={7}>
                  CSV should include columns: name, email, role (optional), team (optional)
              </Text>
            </div>
          </Group>
        </Dropzone>
      ) : (
        <>
          <ScrollArea h={300} mb={16}>
            <Table striped>
              <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Team</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, index) => (
                  <tr key={index}>
                    <td>{user.name || "-"}</td>
                    <td>{user.email || "-"}</td>
                    <td>{user.role || "Default"}</td>
                    <td>{user.team || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </ScrollArea>
          <Button
            variant="outline"
            size="xs"
            onClick={() => {
              setUsers([]);
            }}
          >
            Clear & Upload New File
          </Button>
        </>
      )}

      {users.length > 0 && (
        <Flex align="center" mt={16}>
          <Text mr={10}>Send email invitations</Text>
          <Switch
            checked={sendInvites}
            onChange={(event) => setSendInvites(event.currentTarget.checked)}
          />
        </Flex>
      )}

      <Group position="apart" mt={20}>
        {validUsers.length > 0 ? (
          <Text size="sm" color="dimmed">
            {validUsers.length} valid user{validUsers.length !== 1 ? "s" : ""} to invite
          </Text>
        ) : <Flex justify="start" mt={12}>
          <Button 
            variant="subtle" 
            color="indigo"
            leftIcon={<IconDownload size={16} />} 
            compact 
            onClick={downloadTemplate}
          >
          Download CSV Template
          </Button>
        </Flex>}
        <Group>
          <Button variant="default" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            bg="#5C5CEA"
            sx={{
              ":hover": { backgroundColor: "#9f9af8" },
            }}
            onClick={handleSubmit}
            disabled={validUsers.length === 0 || isLoading}
          >
            {isLoading ? <Loader size="xs" /> : "Invite Users"}
          </Button>
        </Group>
      </Group>
    </Modal>
  );
};

export default BulkInvitePopup; 